import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { CheckCircle, XCircle, Eye, EyeOff, RefreshCw } from "lucide-react";

const KEY_FIELDS = [
  { key: "originality_api_key",  label: "Originality.ai API Key",     hint: "For real AI detection" },
  { key: "undetectable_api_key", label: "Undetectable.ai API Key",    hint: "For premium paraphrasing" },
  { key: "gemini_api_key",       label: "Google Gemini API Key",       hint: "Fallback AI detection & paraphrasing" },
  { key: "resend_api_key",       label: "Resend Email API Key",        hint: "For transactional emails" },
  { key: "payfast_merchant_id",  label: "PayFast Merchant ID",         hint: "" },
  { key: "payfast_merchant_key", label: "PayFast Merchant Key",        hint: "" },
  { key: "payfast_passphrase",   label: "PayFast Passphrase",          hint: "Leave blank if not set" },
];

const TEXT_FIELDS = [
  { key: "admin_email",   label: "Admin Email",    hint: "Account with this email gets admin access" },
  { key: "frontend_url",  label: "Frontend URL",   hint: "e.g. https://academichumanize.com" },
  { key: "backend_url",   label: "Backend URL",    hint: "e.g. https://humanizeai-backend-production.up.railway.app" },
];

function KeyRow({ field, status, onSave }) {
  const [val, setVal] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!val.trim()) { toast.error("Enter a value"); return; }
    setSaving(true);
    try {
      await onSave(field.key, val.trim());
      setVal("");
      toast.success(`${field.label} updated`);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-sm">{field.label}</span>
          {field.hint && <p className="text-xs text-zinc-500 mt-0.5">{field.hint}</p>}
        </div>
        {status?.configured
          ? <span className="flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle size={13}/> Configured</span>
          : <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle size={13}/> Not set</span>}
      </div>
      {status?.configured && status.masked && (
        <p className="text-xs text-zinc-500 font-mono">{status.masked}</p>
      )}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type={show ? "text" : "password"}
            placeholder="Enter new value to update…"
            value={val}
            onChange={e => setVal(e.target.value)}
            className="pr-9 text-sm"
            onKeyDown={e => e.key === "Enter" && save()}
          />
          <button type="button" onClick={() => setShow(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
            {show ? <EyeOff size={14}/> : <Eye size={14}/>}
          </button>
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 shrink-0">
          {saving ? "…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function TextField({ field, status, onSave }) {
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!val.trim()) { toast.error("Enter a value"); return; }
    setSaving(true);
    try {
      await onSave(field.key, val.trim());
      setVal("");
      toast.success(`${field.label} updated`);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium text-sm">{field.label}</span>
          {field.hint && <p className="text-xs text-zinc-500 mt-0.5">{field.hint}</p>}
        </div>
        {status?.configured
          ? <span className="flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle size={13}/> Set</span>
          : <span className="flex items-center gap-1 text-zinc-500 text-xs"><XCircle size={13}/> Not set</span>}
      </div>
      {status?.value && (
        <p className="text-xs text-zinc-400 font-mono truncate">{status.value}</p>
      )}
      <div className="flex gap-2">
        <Input
          type="text"
          placeholder="Enter new value…"
          value={val}
          onChange={e => setVal(e.target.value)}
          className="text-sm flex-1"
          onKeyDown={e => e.key === "Enter" && save()}
        />
        <Button size="sm" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 shrink-0">
          {saving ? "…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [s, setS] = useState({});
  const [keys, setKeys] = useState({});
  const [sandbox, setSandbox] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);

  useEffect(() => {
    api.get("/settings/plan-limits").then(r => setS(r.data)).catch(() => {});
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoadingKeys(true);
    try {
      const r = await api.get("/admin/settings/api-keys");
      setKeys(r.data);
      setSandbox(r.data?.payfast_sandbox?.value ?? true);
    } catch { toast.error("Failed to load API keys"); }
    finally { setLoadingKeys(false); }
  };

  const savePlanLimits = async () => {
    await api.put("/admin/settings", s);
    toast.success("Settings saved");
  };

  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  const num = (k) => (e) => set(k, Number(e.target.value));

  const saveKey = async (field, value) => {
    await api.put("/admin/settings/api-keys", { [field]: value });
    await loadKeys();
  };

  const saveSandbox = async (v) => {
    setSandbox(v);
    await api.put("/admin/settings/api-keys", { payfast_sandbox: v });
    toast.success(`PayFast ${v ? "sandbox" : "live"} mode saved`);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-14 space-y-10">
      <div>
        <span className="px-2 py-1 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-[10px] tracking-widest uppercase">ADMIN</span>
        <h1 className="mt-2 text-3xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Settings</h1>
      </div>

      {/* Plan limits */}
      <div className="glass-strong rounded-2xl p-6 space-y-5">
        <h3 className="font-semibold">Plan limits</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs text-zinc-400">Free max words</Label><Input type="number" value={s.free_maxWords||0} onChange={num("free_maxWords")} className="mt-1"/></div>
          <div><Label className="text-xs text-zinc-400">Free scans/day</Label><Input type="number" value={s.free_maxScansPerDay||0} onChange={num("free_maxScansPerDay")} className="mt-1"/></div>
          <div><Label className="text-xs text-zinc-400">Free rounds</Label><Input type="number" value={s.free_maxRounds||0} onChange={num("free_maxRounds")} className="mt-1"/></div>
          <div><Label className="text-xs text-zinc-400">Pro max words</Label><Input type="number" value={s.pro_maxWords||0} onChange={num("pro_maxWords")} className="mt-1"/></div>
          <div><Label className="text-xs text-zinc-400">Business max words</Label><Input type="number" value={s.business_maxWords||0} onChange={num("business_maxWords")} className="mt-1"/></div>
          <div><Label className="text-xs text-zinc-400">Business batch docs</Label><Input type="number" value={s.business_maxBatchDocs||0} onChange={num("business_maxBatchDocs")} className="mt-1"/></div>
        </div>
        <Button onClick={savePlanLimits} className="bg-indigo-600 hover:bg-indigo-700 glow-primary">Save plan limits</Button>
      </div>

      {/* Maintenance mode */}
      <div className="glass-strong rounded-2xl p-6 flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Maintenance mode</h3>
          <p className="text-sm text-zinc-400 mt-1">Show maintenance page to non-admin users.</p>
        </div>
        <Switch checked={!!s.maintenanceMode} onCheckedChange={v => set("maintenanceMode", v)}/>
      </div>

      {/* API Keys */}
      <div className="glass-strong rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">API Keys</h3>
            <p className="text-sm text-zinc-400 mt-1">
              Keys saved here override environment variables without redeploying.
            </p>
          </div>
          <button onClick={loadKeys} className="text-zinc-500 hover:text-zinc-300 transition-colors">
            <RefreshCw size={16} className={loadingKeys ? "animate-spin" : ""}/>
          </button>
        </div>

        {loadingKeys ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : (
          <div className="space-y-3">
            {KEY_FIELDS.map(f => (
              <KeyRow key={f.key} field={f} status={keys[f.key]} onSave={saveKey}/>
            ))}

            {/* PayFast sandbox toggle */}
            <div className="glass rounded-xl p-4 flex items-center justify-between">
              <div>
                <span className="font-medium text-sm">PayFast Sandbox Mode</span>
                <p className="text-xs text-zinc-500 mt-0.5">Enable for testing. Disable for live payments.</p>
              </div>
              <Switch checked={sandbox} onCheckedChange={saveSandbox}/>
            </div>

            {TEXT_FIELDS.map(f => (
              <TextField key={f.key} field={f} status={keys[f.key]} onSave={saveKey}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
